/**
 * Qué pantalla pide la URL — el único sitio que lee la barra de direcciones, y
 * el único donde están escritas las rutas de esta aplicación.
 *
 * ## Por qué existe
 *
 * Hasta el 2026-10-07 `App.tsx` comparaba la ruta con `===` cinco veces:
 *
 * ```ts
 * if (window.location.pathname === RUTA_DECLARACION) ...
 * ```
 *
 * Eso acierta con la ruta exacta y falla en silencio con todo lo demás.
 * `/interno/` con barra final, `/Interno` como lo escribe quien lo recuerda a
 * medias, o `/%69nterno` como lo deja algún cliente de correo al rehacer un
 * enlace, no son ninguna de las cinco, así que caían al formulario de entrada
 * **sin decir nada**. Quien lo escribió bien salvo una barra no tiene forma de
 * saber qué ha pasado.
 *
 * ## Y por qué NO hay un parámetro `?volver=`
 *
 * Es la decisión de seguridad de este fichero, y va escrita porque el día que
 * alguien necesite devolver a una pantalla concreta va a querer inventarlo.
 *
 * ```text
 * LO QUE SE HACE    el destino se deduce de la RUTA que se pidio, y se
 *                   busca en una lista cerrada. Si no esta en la lista,
 *                   no hay destino
 *
 * LO QUE NO         aceptar un destino que venga en la URL como texto
 *                   libre. Eso es un redirect abierto, y la lista de
 *                   formas de colarlo -//otro.com, /\otro.com, %2f%2f,
 *                   una barra invertida, un javascript:- es mas larga que
 *                   la lista entera de rutas de esta aplicacion
 * ```
 *
 * Con una lista cerrada el problema deja de ser «¿he validado bien este texto?»
 * y pasa a ser una búsqueda en un mapa, que no tiene casos límite. Validar
 * texto libre sería más flexible, y es exactamente la flexibilidad que no hace
 * falta: esta aplicación no tiene router y sus rutas se cuentan con una mano
 * (`Master §8`).
 *
 * ## Lo que un destino NO es
 *
 * **No concede nada.** `operador` dice qué pantalla abrir, no que quien la abra
 * sea operador: eso lo decide la API en cada petición a `/api/v1/internal/*`
 * (`OD-0020`). Quien no lo sea llega a la pantalla y la ve vacía, con «no se ha
 * encontrado», que es lo correcto.
 */

/** Las rutas que esta aplicación reconoce. Todo lo demás es la raíz. */
export type RutaConocida =
  | "entrar"
  | "registro"
  | "declaracion"
  | "como-autorizar"
  | "operador"
  | "plan"
  | "instalar";

/**
 * El camino de cada una. **Única fuente**: `App.tsx` también lee de aquí los
 * que usa como `href`, para que no pueda haber dos verdades sobre una URL.
 *
 * ```text
 * entrar          la entrada, con URL propia. Antes no la tenia: entrar era
 *                 lo que pasaba en la raiz si no habia sesion, y eso vale
 *                 hasta que algo necesita ENLAZAR a la entrada -el sitio
 *                 publico, un correo, una pantalla que te ha echado-
 * registro        donde aterriza quien llega del sitio publico a darse de
 *                 alta. URL propia y no un boton dentro del login: quien
 *                 pulsa «Crear cuenta» viene a crear una cuenta, y pasarle
 *                 antes por un formulario que no puede rellenar -no tiene
 *                 cuenta- se paga en altas perdidas
 * declaracion     Q-04: la declaracion responsable, publica y sin sesion
 * como-autorizar  la guia del apoderamiento (OD-0039 E-10). Se abre en OTRA
 *                 pestana desde la puerta: quien esta haciendo el tramite en
 *                 la Sede necesita las instrucciones a la vista
 * operador        herramientas internas (OD-0020 D-02). No se enlaza desde
 *                 ninguna pantalla
 * plan            a donde devuelve Stripe el navegador tras pagar o cancelar
 *                 (OD-0014 A-10). La compone el SERVIDOR, asi que este valor
 *                 tiene que seguir siendo `/plan`
 * instalar        instalar la aplicacion en el ordenador (OD-0065, ampliacion
 *                 del 2026-10-25). Sin sesion: llega desde el boton
 *                 «Descarga la aplicacion de escritorio» de faktelio.com. El
 *                 navegador solo instala el origen de la pagina, asi que el
 *                 boton tiene que estar AQUI y no en el sitio publico
 * ```
 *
 * Todas las sirve el mismo `index.html`, porque el Worker del sitio declara
 * `not_found_handling = "single-page-application"`. Sin eso, ninguna de estas
 * rutas existe: el navegador pide `/interno` y recibe un 404 del borde, y nada
 * de este fichero llega a ejecutarse.
 *
 * Los caminos van ya en su forma canónica —minúsculas, sin barra final—, que es
 * con la que `leerRuta` compara. Hay una comprobación que lo verifica, porque
 * escribir aquí `/Interno` dejaría esa ruta inalcanzable y nada más lo diría.
 */
export const CAMINO: Readonly<Record<RutaConocida, string>> = {
  entrar: "/entrar",
  registro: "/registro",
  declaracion: "/declaracion-responsable",
  "como-autorizar": "/como-autorizar",
  operador: "/interno",
  plan: "/plan",
  instalar: "/instalar",
};

/** La lista cerrada, del revés: camino → pantalla. */
const RUTAS: Readonly<Record<string, RutaConocida>> = Object.fromEntries(
  Object.entries(CAMINO).map(([nombre, camino]) => [camino, nombre as RutaConocida]),
);

/**
 * Las que no se pueden pintar sin sesión.
 *
 * Que una ruta esté aquí significa «antes de abrir esto, pregunta a la API si
 * hay sesión; si no la hay, enseña la entrada y vuelve aquí después». No
 * significa que la pantalla sea segura por estar en la lista: lo que la protege
 * es que la API comprueba los permisos en cada petición.
 */
export const PROTEGIDAS: ReadonlySet<RutaConocida> = new Set<RutaConocida>(["operador"]);

/**
 * Las rutas que se borran de la barra de direcciones al cargar.
 *
 * ```text
 * plan      si se quedara, recargar volveria a decir «estamos confirmando
 *           tu pago» dias despues, y compartir la URL ensenaria ese
 *           mensaje a otra persona
 * registro  una vez creada la cuenta, recargar devolveria al formulario de
 *           alta a alguien que ya se dio de alta
 * entrar    lo mismo, por el mismo motivo
 * ```
 *
 * `operador` **no está**, y es la diferencia que importa: ahí la URL es el sitio
 * donde estás, no un paso por el que pasaste. Recargar tiene que dejarte donde
 * estabas, y por eso se puede guardar en marcadores.
 */
export const SE_LIMPIA: ReadonlySet<RutaConocida> = new Set<RutaConocida>(["plan", "registro", "entrar"]);

/**
 * Deja una ruta en su forma canónica, o `null` si no se puede.
 *
 * El orden importa, y es el único que funciona:
 *
 * ```text
 * 1 fuera los caracteres que el navegador YA quita       \t \n \r
 * 2 las barras invertidas pasan a barras                 /\otro -> //otro
 * 3 se descodifica UNA vez                               %2f -> /
 * 4 se colapsan las barras repetidas y se quita la final
 * 5 a minusculas
 * ```
 *
 * Descodificar **antes** de comparar es lo que impide que `/%69nterno` cuente
 * como una ruta distinta. Descodificar **una sola vez** es lo que impide lo
 * contrario: `%252f` significa el texto `%2f`, y volver a pasarlo lo
 * convertiría en una barra que nadie escribió.
 *
 * Los `..` NO se resuelven, a propósito: `/interno/../otro` se queda como está,
 * no coincide con nada de la lista y por tanto no abre nada. Resolverlos sería
 * escribir un normalizador de rutas, que es justo donde viven estos fallos.
 */
export function normalizarRuta(pathname: string): string | null {
  const sinControl = pathname.replace(/[\t\n\r\0]/g, "");

  let decodificada: string;
  try {
    decodificada = decodeURIComponent(sinControl.replace(/\\/g, "/"));
  } catch {
    // `%zz` y compañía: una ruta que no es una ruta. No se adivina cuál sería.
    return null;
  }

  const colapsada = decodificada.replace(/\/{2,}/g, "/").replace(/\/+$/, "");
  return (colapsada === "" ? "/" : colapsada).toLowerCase();
}

/**
 * Qué pantalla pide esta ruta, o `null` si ninguna —y entonces es la raíz.
 *
 * `null` es la respuesta correcta para todo lo que no esté en la lista, incluida
 * cualquier cosa con pinta de dirección de otro sitio. No se intenta adivinar a
 * qué se parecía.
 */
export function leerRuta(pathname: string): RutaConocida | null {
  const ruta = normalizarRuta(pathname);
  if (ruta === null) return null;
  return Object.prototype.hasOwnProperty.call(RUTAS, ruta) ? (RUTAS[ruta] ?? null) : null;
}
